<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import Zinput from '../_common/z/zinput.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import dayjs from 'dayjs'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import { PurchaseTypeFriendly } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { ChangeMethodInputDto, ChangeMethodOutputDto } from '../../modules/paymentsApi/purchases/dto/change-method.dto'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let purchaseId: string

    let entity: ChangeMethodInputDto = new ChangeMethodInputDto()
    let purchase: Purchase = null
    let gateways: Gateway[] = []

    let step = 1
    let saving = false
    let show = true
    let simulation: ChangeMethodOutputDto = null

    // Destinos possíveis. Cartão nunca entra: a troca é feita pelo operador, e ninguém aqui tem o
    // número do cartão nem o código de segurança — passar para crédito exigiria o titular. Sair do
    // cartão é permitido; o cartão salvo apenas deixa de ser cobrado.
    const DESTINOS = [PaymentMethods.pix, PaymentMethods.bankslip]

    async function load() {
        try {
            show = true
            purchase = await window.api.payments.purchases.getOne(purchaseId, { invoices: true })
            gateways = await window.api.payments.gateways.getAll()
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    onMount(async () => await load())

    // Só entram os meios que o gateway DESTA assinatura aceita, e que aceitam este tipo de
    // cobrança. As capacidades vêm calculadas do backend a partir do adapter de cada gateway — não
    // são uma lista mantida aqui, que divergiria da implementação.
    function methodOptions(p: Purchase, lista: Gateway[]): { value: string; label: string }[] {
        if (!p) return []
        const gateway = lista?.find((el) => el.gatewayId === p.gatewayId)
        if (!gateway) return []
        const meios = gateway.gatewayPaymentMethods || []
        return DESTINOS.filter((method) => method !== p.purchaseMethod)
            .filter((method) => !meios.length || meios.includes(method))
            .filter((method) => {
                const tipos = gateway.gatewayPurchaseTypes?.[method]
                return !tipos || tipos.includes(p.purchaseType)
            })
            .map((method) => ({ value: method, label: PaymentMethodsFriendly[method] || method }))
    }

    function methodLabel(method: string): string {
        return PaymentMethodsFriendly[method] || method
    }

    async function goSimulate() {
        try {
            if (!entity.method) {
                window.error('Selecione o novo meio de pagamento')
                return
            }
            entity.simulation = true
            simulation = await window.api.payments.purchases.changeMethod(purchaseId, entity)
            step = 2
        } catch (error) {
            window.error(error.message || error)
        }
    }

    async function goSave() {
        try {
            saving = true
            entity.simulation = false
            await window.api.payments.purchases.changeMethod(purchaseId, entity)
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            saving = false
        }
    }

    $: opcoes = methodOptions(purchase, gateways)
</script>

<div class="root">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.pay.entries.write && opcoes.length > 0}
            {#if step === 1}
                <Zbutton label="Simular" on:click={goSimulate}></Zbutton>
            {:else}
                <Zbutton label="Voltar" color="yellow" on:click={() => (step = 1)}></Zbutton>
                <Zbutton label="Confirmar troca" color="green" loading={saving} disabled={saving} on:click={goSave}></Zbutton>
            {/if}
        {/if}
    </Zact>

    {#if !show && purchase}
        {#if opcoes.length === 0}
            <div class="alert alert-warning text-center m-3">
                O gateway desta cobrança não aceita nenhum outro meio de pagamento no formato
                <strong>{PurchaseTypeFriendly[purchase.purchaseType] || purchase.purchaseType}</strong>. A troca é possível apenas para pix ou boleto.
            </div>
        {:else}
            <div class="alert alert-primary m-3">
                As parcelas ainda não cobradas passam a ser cobradas pelo meio novo. As já emitidas continuam pagáveis pelo meio atual até vencerem — nada
                é cancelado no gateway.
            </div>

            <Zframe title="Alterar meio de pagamento" class="m-3">
                {#if step === 1}
                    <ZContainer gapRow="8px" gapCol="8px">
                        <Zinput size={6} sizeM={12} label="Meio atual" type="text" value={methodLabel(purchase.purchaseMethod)} disabled={true}></Zinput>
                        <Zinput size={6} sizeM={12} label="Novo meio" type="select" options={opcoes} bind:value={entity.method}></Zinput>
                    </ZContainer>
                {:else}
                    <div>
                        De <strong>{methodLabel(simulation.fromMethod)}</strong> para <strong>{methodLabel(simulation.toMethod)}</strong>.
                    </div>

                    <div class="mt-2">
                        {#if simulation.chargesToChange.length === 0}
                            Nenhuma parcela em aberto muda agora. As próximas parcelas geradas já nascem no meio novo.
                        {:else}
                            {simulation.chargesToChange.length} parcela(s) passam a ser cobradas por {methodLabel(simulation.toMethod)}:
                            <ul>
                                {#each simulation.chargesToChange as charge}
                                    <li>
                                        {dayjs(charge.dueDate).format('DD/MM/YYYY')} — {window.api.utils.utils.toReal(charge.value)}
                                    </li>
                                {/each}
                            </ul>
                        {/if}
                    </div>

                    {#if simulation.openAtPreviousMethod.length > 0}
                        <!-- um boleto emitido ou um QR pix seguem pagáveis até vencerem: não cancelamos no gateway -->
                        <div class="alert alert-warning mt-2">
                            Atenção: {simulation.openAtPreviousMethod.length} cobrança(s) já emitida(s) continuam pagáveis pelo meio atual até vencerem.
                            <ul class="mb-0">
                                {#each simulation.openAtPreviousMethod as charge}
                                    <li>
                                        {dayjs(charge.dueDate).format('DD/MM/YYYY')} — {methodLabel(charge.paymentMethod)}
                                    </li>
                                {/each}
                            </ul>
                        </div>
                    {/if}
                {/if}
            </Zframe>
        {/if}
    {/if}
</div>
